// Reine Logik-Schicht für AuthContext.isLoading — bewusst ohne React-/better-auth-
// Imports, damit sie im Vitest-„node"-Runner ohne DOM getestet werden kann.

export interface AuthGateInput {
  /** better-auth-Session wird noch aufgelöst (`useSession().isPending`). */
  sessionPending: boolean;
  isAuthenticated: boolean;
  /** better-auth `banned=true` → Konto wird noch freigeschaltet. */
  isPendingApproval: boolean;
  /** id des aktuell eingeloggten Users (null, solange nicht eingeloggt). */
  currentUserId: string | null;
  /** id, für die die RBAC-Permissions zuletzt aufgelöst wurden (geladen ODER
   *  bewusst übersprungen); null vor dem ersten Auflösen. */
  resolvedUserId: string | null;
  /** ein (manueller) `reloadPermissions()`-Fetch läuft gerade. */
  permissionsFetching: boolean;
}

/**
 * Einzige Wahrheitsquelle für `AuthContext.isLoading`.
 *
 * Liefert `true`, solange `hasPermission()` noch nicht vertrauenswürdig ist —
 * damit `RequireAuth` den „Berechtigung wird geprüft…"-Spinner zeigt, statt bei
 * einem noch leeren `permissions`-Array durchzufallen und fälschlich nach
 * `/access-denied` zu leiten.
 *
 * Der kritische Fall ist die **Reload-Race**: better-auth löst die Session auf
 * (`sessionPending` → false, `isAuthenticated` → true) EINEN Render, bevor der
 * Permissions-`useEffect` läuft. Weil „aufgelöst" an der **User-id** hängt (nicht
 * an einem Boolean-Flag, das über den Übergang hinweg `true` leaken kann), gilt
 * in genau diesem Render `resolvedUserId (null) !== currentUserId` → wir melden
 * sofort „loading". Kein durchgesickerter „ready"-Render, kein Fehl-Redirect.
 */
export function computeAuthLoading(i: AuthGateInput): boolean {
  // 1) Session selbst wird noch geladen → immer loading.
  if (i.sessionPending) return true;
  // 2) Session aufgelöst & nicht eingeloggt → fertig (RequireAuth: → /login).
  if (!i.isAuthenticated) return false;
  // 3) Freischaltung ausstehend → fertig (RequireAuth: → /pending-approval).
  //    Permissions werden für diese User bewusst NICHT geladen.
  if (i.isPendingApproval) return false;
  // 4) Manueller Reload der Permissions läuft → loading.
  if (i.permissionsFetching) return true;
  // 5) Eingeloggt & aktiv: loading, bis die Permissions für DIESEN User
  //    aufgelöst sind. Nach Login-/User-Wechsel ist resolvedUserId noch der
  //    alte Wert (oder null) → sofort loading, ohne auf den Effect zu warten.
  return i.resolvedUserId !== i.currentUserId;
}
